import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import type React from 'react';

type SwitchGroupProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

export function SwitchGroup({ className, ...props }: SwitchGroupProps) {
  return (
    <div
      data-slot="control"
      {...props}
      className={clsx(
        className,
        // Basic groups
        'tw:space-y-3 [&_[data-slot=label]]:tw:font-normal',
        // With descriptions
        'tw:has-[[data-slot=description]]:space-y-6 tw:[&_[data-slot=label]]:tw:has-[[data-slot=description]]:font-medium'
      )}
    />
  );
}

export function SwitchField({
  className,
  ...props
}: { className?: string } & Omit<Headless.FieldProps, 'as' | 'className'>) {
  return (
    <Headless.Field
      data-slot="field"
      {...props}
      className={clsx(
        className,
        // Base layout
        'tw:grid tw:grid-cols-[1fr_auto] tw:items-center tw:gap-x-8 tw:gap-y-1 tw:sm:grid-cols-[1fr_auto]',
        // Control layout
        'tw:[&>[data-slot=control]]:tw:col-start-2 tw:[&>[data-slot=control]]:tw:self-center',
        // Label layout
        'tw:[&>[data-slot=label]]:tw:col-start-1 tw:[&>[data-slot=label]]:tw:row-start-1 tw:[&>[data-slot=label]]:tw:justify-self-start',
        // Description layout
        'tw:[&>[data-slot=description]]:tw:col-start-1 tw:[&>[data-slot=description]]:tw:row-start-2',
        // With description
        'tw:[&_[data-slot=label]]:has-[[data-slot=description]]:font-medium'
      )}
    />
  );
}

export function BrandSwitch({
  className,
  ...props
}: {
  className?: string;
} & Omit<Headless.SwitchProps, 'as' | 'className' | 'children'>) {
  return (
    <Headless.Switch
      data-slot="control"
      {...props}
      className={clsx(
        className,
        // Base styles
        'tw:group tw:relative tw:isolate tw:inline-flex tw:h-6 tw:w-10 tw:cursor-default tw:!rounded-full tw:p-[3px] tw:sm:h-5 tw:sm:w-8',
        // Transitions
        'tw:transition tw:duration-0 tw:ease-in-out tw:data-[changing]:duration-200',
        // Outline and background color in forced-colors mode so switch is still visible
        'tw:forced-colors:outline tw:forced-colors:[--switch-bg:Highlight] tw:dark:forced-colors:[--switch-bg:Highlight]',
        // Unchecked
        'tw:bg-zinc-200 tw:ring-1 tw:ring-inset tw:ring-black/5 tw:dark:bg-white/5 tw:dark:ring-white/15',
        // Checked
        'tw:data-[checked]:bg-brand-green-0 tw:data-[checked]:ring-brand-green-0/90 tw:dark:data-[checked]:bg-brand-green-1 tw:dark:data-[checked]:ring-brand-green-1/90',
        // Focus
        'tw:focus:outline-none tw:data-[focus]:outline tw:data-[focus]:outline-2 tw:data-[focus]:outline-offset-2 tw:data-[focus]:outline-brand-green-0',
        // Hover
        'tw:data-[hover]:data-[checked]:ring-brand-green-0/90 tw:data-[hover]:ring-black/15',
        'tw:dark:data-[hover]:data-[checked]:ring-brand-green-1/90 tw:dark:data-[hover]:ring-white/25',
        // Disabled
        'tw:data-[disabled]:bg-zinc-200 tw:data-[disabled]:data-[checked]:bg-brand-green-0/90 tw:data-[disabled]:opacity-90 tw:data-[disabled]:data-[checked]:ring-black/5',
        'tw:dark:data-[disabled]:bg-white/15 tw:dark:data-[disabled]:data-[checked]:bg-white/15 tw:dark:data-[disabled]:data-[checked]:ring-white/15'
      )}
    >
      <span
        aria-hidden="true"
        className={clsx(
          // Basic layout
          'tw:pointer-events-none tw:relative tw:inline-block tw:size-[1.125rem] tw:rounded-full tw:sm:size-3.5',
          // Transition
          'tw:translate-x-0 tw:transition tw:duration-200 tw:ease-in-out',
          // Invisible border so the switch is still visible in forced-colors mode
          'tw:border tw:border-transparent',
          // Unchecked
          'tw:bg-white tw:shadow tw:ring-1 tw:ring-black/5',
          // Checked
          'tw:group-data-[checked]:bg-white tw:group-data-[checked]:shadow-brand-green-0/20 tw:group-data-[checked]:ring-brand-green-0/90',
          'tw:dark:group-data-[checked]:bg-white tw:dark:group-data-[checked]:shadow-brand-green-1/20 tw:dark:group-data-[checked]:ring-brand-green-1/90',
          'tw:group-data-[checked]:translate-x-4 tw:sm:group-data-[checked]:translate-x-3',
          // Disabled
          'tw:group-data-[disabled]:group-data-[checked]:bg-white tw:group-data-[disabled]:group-data-[checked]:shadow tw:group-data-[disabled]:group-data-[checked]:ring-black/5'
        )}
      />
    </Headless.Switch>
  );
}
